import { useConversation } from '@elevenlabs/react';
import { Box, lighten, Tooltip, withStyles } from '@material-ui/core';
import { setPendingRedirect } from '@src/reducers/conversationalAgent';
import { classifyRedirectUrl, RedirectKind } from '@src/utils/url';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import AIBubble from './AIBubble';
import RedirectSnackbar from './RedirectSnackbar';

// ─── Call duration configuration ─────────────────────────────────────────
// The ONLY value to change if the call time limit changes.
// Reminder: the "Max conversation duration" in the ElevenLabs dashboard
// must be this value + ~30-60s of headroom (hard-cutoff safety net).
const MAX_CALL_DURATION_SECONDS = 600; // 10 minutes

// Notices defined as seconds BEFORE the limit is reached, so they
// automatically shift when MAX_CALL_DURATION_SECONDS changes.
// 'context' updates don't trigger an immediate agent response (they are
// incorporated on its next turn); 'message' forces the agent to take a
// turn even if there is silence.
const TIME_NOTICES = [
  {
    secondsBeforeEnd: 120, // gentle heads-up
    kind: 'context' as const,
    text: '[CONTROL DE TIEMPO] Quedan menos de 2 minutos de llamada. Sé más conciso y no abras temas nuevos extensos.',
  },
  {
    secondsBeforeEnd: 60, // start wrapping up
    kind: 'context' as const,
    text: '[CONTROL DE TIEMPO] Queda menos de 1 minuto. En tu próximo turno resume lo acordado, despídete cordialmente y usa la herramienta finishConversation.',
  },
  {
    secondsBeforeEnd: 30, // force the goodbye (triggers an agent turn)
    kind: 'message' as const,
    text: '[MENSAJE AUTOMÁTICO DEL SISTEMA, NO ES EL CLIENTE] El tiempo se agotó. Despídete ahora en una o dos frases y usa la herramienta finishConversation inmediatamente.',
  },
]
  .map(notice => ({
    ...notice,
    fireAtMs: (MAX_CALL_DURATION_SECONDS - notice.secondsBeforeEnd) * 1000,
  }))
  // Drop notices that don't fit when the limit is very short (e.g. < 2 min)
  .filter(notice => notice.fireAtMs > 0);
// ─────────────────────────────────────────────────────────────────────────

const ConversationalAgent: React.FC = () => {
  const { pathname } = useLocation();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const [listening, setListening] = useState(false);
  const [started, setStarted] = useState(false);

  // Ref to the conversation instance so clientTools defined below can
  // invoke its methods without hitting the TDZ on `conversation`.
  const conversationRef = useRef<ReturnType<typeof useConversation> | null>(
    null
  );

  const timeNoticeTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Tracks whether the session is live. We own this flag instead of relying
  // on `conversation.status`: in @elevenlabs/react@0.7.0 the hook reports
  // 'disconnected' even during an active call, which silently swallowed
  // every time notice behind the previous status-based guard.
  const sessionActiveRef = useRef(false);

  const clearTimeNotices = () => {
    timeNoticeTimersRef.current.forEach(clearTimeout);
    timeNoticeTimersRef.current = [];
  };

  const scheduleTimeNotices = () => {
    clearTimeNotices(); // in case anything is left over from a previous session
    timeNoticeTimersRef.current = TIME_NOTICES.map(({ fireAtMs, kind, text }) =>
      setTimeout(() => {
        const conv = conversationRef.current;
        // The session may have ended (or dropped) while this timer was
        // pending; only send over a live connection.
        if (!sessionActiveRef.current || !conv) return;
        if (kind === 'context') {
          conv.sendContextualUpdate(text);
        } else {
          conv.sendUserMessage(text);
        }
      }, fireAtMs)
    );
  };

  const conversation = useConversation({
    clientTools: {
      redirectToUrl: ({ url }: { url: string; [key: string]: unknown }) => {
        const classified = classifyRedirectUrl(url);
        if (classified.kind === RedirectKind.Invalid) {
          // eslint-disable-next-line no-console
          console.error(
            'ConversationalAgent: ignoring invalid redirect URL:',
            url
          );
          return;
        }
        // In-app routes navigate right away; only external links go through
        // the confirmation toast (they open a new tab, which needs consent).
        if (classified.kind === RedirectKind.Internal) {
          history.push(classified.url);
          return;
        }
        dispatch(
          setPendingRedirect({ url: classified.url, requestId: Date.now() })
        );
      },
      // @elevenlabs/client@0.7.0 doesn't route the built-in `end_call`
      // system tool through onDisconnect / onStatusChange, so the agent
      // dashboard must use a Custom Client Tool instead. When the agent
      // invokes `finishConversation`, end the session ourselves so the
      // SDK's normal disconnect path fires (LiveKit close → onDisconnect
      // → status='disconnected').
      finishConversation: () => {
        // eslint-disable-next-line no-console
        console.log('Receive Call finishConversation');
        // Cancel pending time notices so none fires on a dying session.
        sessionActiveRef.current = false;
        clearTimeNotices();
        conversationRef.current?.endSession();
      },
    },
  });

  conversationRef.current = conversation;

  const getTooltipText = () => {
    if (!started)
      return formatMessage({ id: 'ConversationalAgent.greetingQuestion' });
    if (listening)
      return formatMessage({
        id: 'ConversationalAgent.finishConversationTooltip',
      });
    return '';
  };
  const tooltipText = getTooltipText();

  async function startConversation() {
    if (started) return;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      await conversation.startSession({
        agentId: process.env.REACT_APP_ELEVENLABS_AGENT_ID as string,
        connectionType: 'webrtc',
        dynamicVariables: {
          redirect_target_url:
            process.env.REACT_APP_ELEVENLABS_REDIRECT_URL ?? '',
        },
        workletPaths: {
          audioConcatProcessor: '/worklets/audioConcatProcessor.js',
          rawAudioProcessor: '/worklets/rawAudioProcessor.js',
        },
        onConnect: () => {
          sessionActiveRef.current = true;
          setStarted(true);
          scheduleTimeNotices(); // the clock starts with the actual connection
        },
        onDisconnect: (...args: unknown[]) => {
          // eslint-disable-next-line no-console
          console.warn('ConversationalAgent disconnected:', ...args);
          sessionActiveRef.current = false;
          clearTimeNotices();
          setListening(false);
          setStarted(false);
        },
        onError: (message: string, context?: unknown) => {
          // eslint-disable-next-line no-console
          console.error('Error:', message, context);
          sessionActiveRef.current = false;
          clearTimeNotices();
        },
        onModeChange: ({ mode }: { mode: string }) => {
          setListening(mode === 'listening');
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to start conversation:', error);
      sessionActiveRef.current = false;
      clearTimeNotices();
    }
  }

  function stopConversation() {
    sessionActiveRef.current = false;
    clearTimeNotices();
    conversation.endSession();
  }

  useEffect(
    () => stopConversation,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (pathname.includes('/agent/chat')) {
    return null;
  }

  return (
    <StyledTooltip
      title={tooltipText}
      placement="top"
      PopperProps={{
        modifiers: {
          offset: {
            enabled: true,
            offset: '-120, -15',
          },
          flip: {
            enabled: false,
          },
          preventOverflow: {
            enabled: false,
          },
        },
      }}
    >
      <Box style={{ position: 'relative' }}>
        <AIBubble
          isTalking={started && !listening}
          isListening={listening}
          onClick={() => (started ? stopConversation() : startConversation())}
        />
        <RedirectSnackbar />
      </Box>
    </StyledTooltip>
  );
};

const StyledTooltip = withStyles(() => ({
  tooltip: {
    border: `0.5px solid #EAF6FF`,
    color: '#478ECC',
    padding: '4px 16px',
    backgroundColor: lighten('#478ECC', 0.9),
    fontWeight: 500,
  },
  arrow: {
    color: lighten('#478ECC', 0.9),
    '&:before': {
      border: `0.5px solid #EAF6FF`,
    },
  },
}))(Tooltip);

export default ConversationalAgent;
